import React, { useEffect, useRef, useState } from "react";

import { toast } from "react-toastify";
import {
	Trash2,
	Upload,
	Loader2,
	FileText,
	FileAudio,
	FileVideo,
	File as FileIcon,
	Package,
} from "lucide-react";

import { Button } from "../../components/ui/button";
import { Card, CardContent } from "../../components/ui/card";
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogDescription,
	DialogFooter,
} from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Skeleton } from "../../components/ui/skeleton";
import { Checkbox } from "../../components/ui/checkbox";
import { Textarea } from "../../components/ui/textarea";
import { cn } from "../../lib/utils";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import ConfirmationModal from "../../components/ConfirmationModal";
import toastError from "../../errors/toastError";
import api from "../../services/api";
import { getPublicFileUrl } from "../../config";

const mediaIcon = (mediaType) => {
	if (mediaType?.startsWith("video")) {
		return <FileVideo className="h-10 w-10 text-muted-foreground" />;
	}
	if (mediaType?.startsWith("audio")) {
		return <FileAudio className="h-10 w-10 text-muted-foreground" />;
	}
	if (mediaType?.startsWith("application") || mediaType === "document") {
		return <FileText className="h-10 w-10 text-muted-foreground" />;
	}
	return <FileIcon className="h-10 w-10 text-muted-foreground" />;
};

const MediaLibrary = () => {
	const [items, setItems] = useState([]);
	// Seleção para montar um pacote. Guarda ids na ordem em que foram
	// clicados: é ela que define a sequência de envio.
	const [selecionados, setSelecionados] = useState([]);
	const [packModalOpen, setPackModalOpen] = useState(false);
	const [packName, setPackName] = useState("");
	const [packDescription, setPackDescription] = useState("");
	const [salvandoPack, setSalvandoPack] = useState(false);
	const [loading, setLoading] = useState(false);

	const [uploadModalOpen, setUploadModalOpen] = useState(false);
	const [uploadName, setUploadName] = useState("");
	const [uploadFile, setUploadFile] = useState(null);
	const [uploading, setUploading] = useState(false);
	const fileInputRef = useRef(null);

	const [selectedItem, setSelectedItem] = useState(null);
	const [confirmModalOpen, setConfirmModalOpen] = useState(false);

	useEffect(() => {
		(async () => {
			setLoading(true);
			try {
				const { data } = await api.get("/media-library");
				setItems(data || []);
				setLoading(false);
			} catch (err) {
				toastError(err);
				setLoading(false);
			}
		})();
	}, []);

	const handleOpenUploadModal = () => {
		setUploadName("");
		setUploadFile(null);
		setUploadModalOpen(true);
	};

	const handleCloseUploadModal = () => {
		setUploadModalOpen(false);
		setUploadName("");
		setUploadFile(null);
	};

	const handleFileChange = (e) => {
		const escolhidos = Array.from(e.target.files || []);
		if (escolhidos.length === 0) return;

		setUploadFile(escolhidos);

		// O nome digitado só vale quando é um arquivo. Com vários, cada um
		// entra com o nome do próprio arquivo — repetir um rótulo em dez itens
		// deixaria a biblioteca impossível de procurar.
		if (escolhidos.length === 1 && !uploadName) {
			setUploadName(escolhidos[0].name);
		}
	};

	const handleUpload = async (e) => {
		e.preventDefault();

		const arquivos = uploadFile || [];

		if (arquivos.length === 0) {
			toast.error("Selecione um arquivo.");
			return;
		}
		// O nome só é exigido quando é um arquivo só; com vários cada um usa o
		// nome do próprio arquivo.
		if (arquivos.length === 1 && !uploadName.trim()) {
			toast.error("Informe um nome.");
			return;
		}

		const formData = new FormData();
		arquivos.forEach((arquivo) => formData.append("medias", arquivo));
		if (arquivos.length === 1) formData.append("name", uploadName);

		setUploading(true);
		try {
			const { data } = await api.post("/media-library", formData);
			// Um arquivo devolve o item; vários devolvem a lista.
			const novos = Array.isArray(data) ? data : [data];
			setItems((prev) => [...novos, ...prev]);
			toast.success(
				novos.length === 1
					? "Arquivo enviado com sucesso!"
					: `${novos.length} arquivos enviados com sucesso!`
			);
			handleCloseUploadModal();
		} catch (err) {
			toastError(err);
		}
		setUploading(false);
	};

	const alternarSelecao = (id) => {
		setSelecionados((atual) =>
			atual.includes(id) ? atual.filter((i) => i !== id) : [...atual, id]
		);
	};

	const criarPacote = async (e) => {
		e.preventDefault();
		setSalvandoPack(true);
		try {
			await api.post("/media-packs", {
				name: packName,
				description: packDescription || null,
				itemIds: selecionados,
			});
			toast.success("Pacote criado. Já dá para enviar num atendimento.");
			setPackModalOpen(false);
			setPackName("");
			setPackDescription("");
			setSelecionados([]);
		} catch (err) {
			toastError(err);
		}
		setSalvandoPack(false);
	};

	const handleDeleteItem = async (itemId) => {
		try {
			await api.delete(`/media-library/${itemId}`);
			setItems((prev) => prev.filter((item) => item.id !== itemId));
			toast.success("Arquivo excluído com sucesso!");
		} catch (err) {
			toastError(err);
		}
		setSelectedItem(null);
	};

	return (
		<MainContainer>
			<ConfirmationModal
				title={selectedItem && `Excluir arquivo ${selectedItem.name}?`}
				open={confirmModalOpen}
				onClose={() => {
					setConfirmModalOpen(false);
					setSelectedItem(null);
				}}
				onConfirm={() => handleDeleteItem(selectedItem.id)}
			>
				Esta ação não pode ser revertida.
			</ConfirmationModal>
			<Dialog
				open={uploadModalOpen}
				onOpenChange={(o) => !o && handleCloseUploadModal()}
			>
				<DialogContent className="sm:max-w-md">
					<DialogHeader>
						<DialogTitle>Enviar arquivo</DialogTitle>
					</DialogHeader>
					<form onSubmit={handleUpload} className="space-y-4">
						<div className="space-y-1.5">
							<Label htmlFor="mediaFile">Arquivos</Label>
							<Input
								id="mediaFile"
								type="file"
								multiple
								ref={fileInputRef}
								onChange={handleFileChange}
							/>
							<p className="text-xs text-muted-foreground">
								Dá para escolher vários de uma vez.
							</p>
						</div>

						{/* Com vários arquivos o campo some: cada um entra com o nome
						    do próprio arquivo. */}
						{(uploadFile?.length ?? 0) <= 1 && (
							<div className="space-y-1.5">
								<Label htmlFor="mediaName">Nome</Label>
								<Input
									id="mediaName"
									value={uploadName}
									onChange={(e) => setUploadName(e.target.value)}
								/>
							</div>
						)}

						{uploadFile?.length > 1 && (
							<div className="rounded-lg border bg-muted/40 p-2.5">
								<p className="text-sm font-medium">
									{uploadFile.length} arquivos escolhidos
								</p>
								<ul className="mt-1 max-h-28 overflow-y-auto text-xs text-muted-foreground">
									{uploadFile.map((arquivo) => (
										<li key={arquivo.name} className="truncate">
											{arquivo.name}
										</li>
									))}
								</ul>
							</div>
						)}
						<DialogFooter>
							<Button
								type="button"
								variant="outline"
								onClick={handleCloseUploadModal}
								disabled={uploading}
							>
								Cancelar
							</Button>
							<Button type="submit" disabled={uploading}>
								{uploading && <Loader2 className="h-4 w-4 animate-spin" />}
								Enviar
							</Button>
						</DialogFooter>
					</form>
				</DialogContent>
			</Dialog>
			<MainHeader>
				<Title>Biblioteca de mídias</Title>
				<MainHeaderButtonsWrapper>
					<Button onClick={handleOpenUploadModal}>
						<Upload className="h-4 w-4" />
						Enviar arquivo
					</Button>
				</MainHeaderButtonsWrapper>
			</MainHeader>
			<div className="flex-1 overflow-auto px-6 pb-6">
				{loading ? (
					<div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5">
						{[...Array(8)].map((_, i) => (
							<Skeleton key={i} className="h-40 rounded-xl" />
						))}
					</div>
				) : items.length === 0 ? (
					<p className="py-10 text-center text-sm text-muted-foreground">
						Nenhum arquivo na biblioteca
					</p>
				) : (
					<>
					{selecionados.length > 0 && (
						<div className="mb-4 flex flex-wrap items-center gap-3 rounded-xl border bg-muted px-4 py-2.5">
							<span className="text-sm font-medium">
								{selecionados.length} arquivo(s) selecionado(s)
							</span>
							<Button
								variant="ghost"
								size="sm"
								onClick={() => setSelecionados([])}
							>
								Limpar
							</Button>
							<Button
								size="sm"
								className="ml-auto"
								onClick={() => setPackModalOpen(true)}
							>
								<Package className="h-4 w-4" />
								Criar pacote
							</Button>
						</div>
					)}
					<div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5">
						{items.map((item) => (
							<Card
								key={item.id}
								className={cn(
									"overflow-hidden transition-colors",
									selecionados.includes(item.id) && "ring-2 ring-primary"
								)}
							>
								<div className="relative flex h-28 items-center justify-center bg-muted">
									<div className="absolute left-2 top-2 z-10">
										<Checkbox
											checked={selecionados.includes(item.id)}
											onCheckedChange={() => alternarSelecao(item.id)}
											aria-label={`Selecionar ${item.name}`}
											className="bg-background"
										/>
									</div>
									{item.mediaType?.startsWith("image") ? (
										<img
											src={getPublicFileUrl(item.mediaUrl)}
											alt={item.name}
											className="h-full w-full object-cover"
										/>
									) : (
										mediaIcon(item.mediaType)
									)}
								</div>
								<CardContent className="flex items-center justify-between gap-2 p-3">
									<span className="truncate text-sm font-medium" title={item.name}>
										{item.name}
									</span>
									<Button
										variant="ghost"
										size="icon"
										className="h-8 w-8 shrink-0 text-destructive hover:text-destructive"
										onClick={() => {
											setSelectedItem(item);
											setConfirmModalOpen(true);
										}}
									>
										<Trash2 className="h-4 w-4" />
									</Button>
								</CardContent>
							</Card>
						))}
					</div>
					</>
				)}
			</div>

			<Dialog open={packModalOpen} onOpenChange={setPackModalOpen}>
				<DialogContent className="sm:max-w-md">
					<form onSubmit={criarPacote}>
						<DialogHeader>
							<DialogTitle>Novo pacote</DialogTitle>
							<DialogDescription>
								Os {selecionados.length} arquivos escolhidos passam a ir juntos,
								nesta ordem, com um clique no atendimento.
							</DialogDescription>
						</DialogHeader>

						<div className="grid gap-3 py-4">
							<div>
								<Label htmlFor="packName">Nome do pacote</Label>
								<Input
									id="packName"
									required
									autoFocus
									placeholder="Ex.: Catálogo de apartamentos"
									value={packName}
									onChange={(e) => setPackName(e.target.value)}
								/>
							</div>
							<div>
								<Label htmlFor="packDescription">
									Mensagem que acompanha{" "}
									<span className="font-normal text-muted-foreground">
										(opcional)
									</span>
								</Label>
								<Textarea
									id="packDescription"
									rows={2}
									placeholder="Segue o material que combinamos."
									value={packDescription}
									onChange={(e) => setPackDescription(e.target.value)}
								/>
								<p className="mt-1 text-xs leading-relaxed text-muted-foreground">
									Vai como legenda do primeiro arquivo. Repetida em todos, o
									cliente leria o mesmo texto várias vezes seguidas.
								</p>
							</div>
						</div>

						<DialogFooter>
							<Button
								type="button"
								variant="ghost"
								onClick={() => setPackModalOpen(false)}
							>
								Cancelar
							</Button>
							<Button type="submit" disabled={salvandoPack}>
								{salvandoPack && <Loader2 className="h-4 w-4 animate-spin" />}
								Criar pacote
							</Button>
						</DialogFooter>
					</form>
				</DialogContent>
			</Dialog>
		</MainContainer>
	);
};

export default MediaLibrary;
